// Warm Up popups — the welcome splash a gated player sees before the guided
// curriculum begins, and the congrats popup that ends it.
//
// ISOLATION CONTRACT: this file is warm-up-owned (delete it + its two calls in
// mountWarmUp and the app is bit-for-bit without it), with ONE dependency: the
// ui/modalReassert leaf primitive. The dialogs built here are re-asserted by
// the resume reconciler on an iOS background→resume; their 'close' listener
// must bail during that re-assert so it doesn't tear the dialog down. That bail
// reads the modalReassert flag — hence the import. It does NOT depend on the
// reconciler itself (acyclic graph). It deliberately does NOT add markup to
// index.html or rules to modal.css — each <dialog> is built here at show time
// and consumes the existing shared modal/button classes (.modal, .modal__inner,
// .modal__title, .modal__actions, .btn--primary) so it looks exactly like the
// "Welcome to Letter Flick!" onboarding popup.
//
// Lifecycle: append → showModal() → button tap (or Escape — the gate must
// never soft-lock, so any close path continues) → close() → remove from the
// DOM → onDone.

import { isReasserting } from '../ui/modalReassert'

// The live warm-up <dialog> currently in the top layer, or null. Tracked so
// isWarmUpDialogOpen() can answer the orchestrator's "is a disruptive modal
// open?" query (a warm-up popup must defer auto-refresh disruptions like the
// other full-screen dialogs). Set on append, cleared on removal.
let activeWarmUpDialog: HTMLDialogElement | null = null

// True iff a warm-up-created <dialog> is currently open AND connected. Used by
// the orchestrator's isAnyDisruptiveModalOpen() so a live re-mount/reload never
// yanks a warm-up popup out from under the player.
export function isWarmUpDialogOpen(): boolean {
  const dialog = activeWarmUpDialog
  return dialog !== null && dialog.open && dialog.isConnected
}

export const WARM_UP_WELCOME_TEXT =
  "New to Letter Flick?\nLet's do a warm up!"

export const WARM_UP_WELCOME_LEAD =
  'Your goal: spell the word to solve the puzzle.'

export const WARM_UP_COMPLETE_TEXT =
  "Well done! You've finished the warm up."

export const WARM_UP_COMPLETE_LEAD =
  "• Tap the help/play button for practice.\n• Daily puzzles get harder Mon - Sun.\n\n• You get 6 undos per puzzle.\n• You get 10 practice puzzles anytime.\n• You get 1 chance at today's puzzle.\n• You get to come back tomorrow!"

// The practice-completion popup title, shown when the player presses NEXT on
// the last practice puzzle (#10). Distinct title from the warm-up congrats
// above; the body reuses WARM_UP_COMPLETE_LEAD (the same six bullets) verbatim.
export const PRACTICE_COMPLETE_TEXT =
  "Well Done! You've finished the practice."

interface WarmUpDialogButton {
  readonly label: string
  readonly action: () => void
  readonly autofocus?: boolean
  // De-emphasised secondary button: rendered as a small, transparent
  // text/link-style control (shared .btn--secondary class) instead of the
  // large blue primary. When ANY button in the set is secondary, the actions
  // render as a row in array order with the small secondary on the left and
  // the half-width blue primary on the right, so the secondary reads as the
  // lesser choice and the primary is the easy thumb target.
  readonly secondary?: boolean
}

// Shared builder for the warm-up popups: a title sentence, an optional lead
// sentence, and one or more buttons. Layout adapts to the button set:
//   - a lone button: centered (the welcome Continue);
//   - two equal blue primaries: side-by-side via modal__actions--row;
//   - a primary + a secondary: a row with the small de-emphasised secondary on
//     the left and the large blue primary (half the modal width) on the right.
// Single-fire continuation: any button tap routes to ITS action; dialog close
// (Escape) routes to onClose — whichever lands first wins, the dialog is
// removed either way (the gate must never soft-lock).
function showWarmUpDialog(opts: {
  readonly titleId: string
  readonly title: string
  readonly lead?: string
  readonly buttons: readonly WarmUpDialogButton[]
  readonly onClose: () => void
}): void {
  const dialog = document.createElement('dialog')
  dialog.className = 'modal'
  dialog.setAttribute('aria-labelledby', opts.titleId)

  const inner = document.createElement('div')
  inner.className = 'modal__inner'

  const title = document.createElement('h2')
  title.id = opts.titleId
  title.className = 'modal__title'
  // \n in the title copy renders as a real line break. Inline style (not a
  // modal.css rule) keeps this warm-up-owned per the isolation contract.
  title.style.whiteSpace = 'pre-line'
  title.textContent = opts.title
  inner.appendChild(title)

  if (opts.lead !== undefined) {
    const lead = document.createElement('p')
    lead.className = 'modal__lead'
    // \n in the lead copy renders as a real line break (each sentence on its
    // own line). Inline style (not a modal.css rule) keeps this warm-up-owned
    // per the isolation contract, same as the title above.
    lead.style.whiteSpace = 'pre-line'
    // One blank line's worth of breathing room between the lead and the button
    // row (margin renders reliably where a trailing blank line in the copy
    // would be dropped as an empty final line box). Inline for the same
    // isolation reason as above.
    lead.style.marginBottom = '1.4em'
    lead.textContent = opts.lead
    inner.appendChild(lead)
  }

  const actions = document.createElement('div')
  // Three layouts: when any button is secondary, a row with the small grey
  // secondary on the LEFT (natural size) and the large blue primary on the
  // RIGHT at half the modal width (thumb-reachable; modal__actions--row would
  // force equal widths, so the base row + inline overrides below are used
  // instead); two equal primaries side-by-side and centered; and the lone
  // Continue (welcome) centered (plain .modal__actions would right-align it).
  const hasSecondary = opts.buttons.some((b) => b.secondary === true)
  if (hasSecondary) {
    actions.className = 'modal__actions'
  } else if (opts.buttons.length > 1) {
    actions.className =
      'modal__actions modal__actions--row modal__actions--center'
  } else {
    actions.className = 'modal__actions modal__actions--center'
  }

  let done = false
  const finish = (action: () => void): void => {
    if (done) return
    done = true
    // Teardown ordering (A2): never detach a still-modal dialog synchronously
    // while it sits in the top layer — that is exactly the state the iOS
    // resume lockup corrupts, and removing a top-layer element can strand the
    // backdrop. So close() FIRST (which demotes it out of the top layer and
    // fires its 'close' event synchronously; our 'close' listener re-enters
    // finish() but the `done` guard above no-ops it), THEN remove() the
    // now-non-modal node. If we arrived here via the 'close' event itself the
    // dialog is already closed, so the close() is skipped and remove() runs on
    // an already-demoted node — still safe.
    if (dialog.open) dialog.close()
    if (activeWarmUpDialog === dialog) activeWarmUpDialog = null
    dialog.remove()
    action()
  }

  // Inline row layout (secondary left, primary right) keeps this
  // warm-up-owned per the isolation contract — no new rule is added to
  // modal.css. Mirrors the existing inline-style approach for the title/lead
  // line breaks.
  if (hasSecondary) {
    actions.style.justifyContent = 'space-between'
    actions.style.alignItems = 'center'
  }

  for (const button of opts.buttons) {
    const el = document.createElement('button')
    el.type = 'button'
    // Secondary buttons get the shared .btn--secondary treatment (transparent
    // fill) plus inline de-emphasis (smaller, lighter, no min-height bump) so
    // they read as a quiet text/link affordance left of the blue primary. In
    // the mixed row the primary takes half the modal width so the encouraged
    // action is the big, easy thumb target. The inline overrides stay
    // warm-up-owned (no new modal.css rules).
    if (button.secondary === true) {
      el.className = 'btn btn--secondary'
      el.style.fontSize = '0.85rem'
      el.style.fontWeight = '400'
      el.style.minHeight = 'auto'
      el.style.padding = '6px 12px'
      el.style.color = 'var(--text-muted)'
    } else {
      el.className = 'btn btn--primary'
      if (hasSecondary) el.style.flex = '0 0 50%'
    }
    el.textContent = button.label
    if (button.autofocus === true) el.autofocus = true
    el.addEventListener('click', (): void => {
      finish(button.action)
    })
    actions.appendChild(el)
  }

  inner.appendChild(actions)
  dialog.appendChild(inner)
  document.body.appendChild(dialog)
  activeWarmUpDialog = dialog

  dialog.addEventListener('close', (): void => {
    // LITERAL FIRST statement: bail before any teardown when the resume
    // reconciler is mid-re-assert. The reconciler's close()→showModal() cycle
    // fires this 'close' event synchronously; running finish() here would tear
    // the dialog down out from under the very re-assert meant to heal it. The
    // re-assert's showModal() restores it; finish() must stay dormant.
    if (isReasserting(dialog)) return
    finish(opts.onClose)
  })

  dialog.showModal()
}

// The welcome splash: shown at warm-up entry, before the first screen.
export function showWarmUpWelcome(onContinue: () => void): void {
  showWarmUpDialog({
    titleId: 'warmup-welcome-title',
    title: WARM_UP_WELCOME_TEXT,
    lead: WARM_UP_WELCOME_LEAD,
    buttons: [{ label: 'Continue', action: onContinue, autofocus: true }],
    onClose: onContinue,
  })
}

// The completion popup: shown after the ZEN finale's win, over the solved
// board. Replaces the former "Well done!" toast. Row fork: a small
// de-emphasised secondary ("skip practice") on the left routes to the daily,
// with the large blue primary ("Let's Practice") on the right routing to
// practice.
// Autofocus lands on the primary practice button. Escape falls through to Play
// (never soft-lock).
export function showWarmUpComplete(
  onPlay: () => void,
  onPractice: () => void,
): void {
  showWarmUpDialog({
    titleId: 'warmup-complete-title',
    title: WARM_UP_COMPLETE_TEXT,
    lead: WARM_UP_COMPLETE_LEAD,
    buttons: [
      { label: 'skip practice', action: onPlay, secondary: true },
      { label: "Let's Practice", action: onPractice, autofocus: true },
    ],
    onClose: onPlay,
  })
}

// The practice-completion popup: shown when the player presses NEXT on the last
// practice puzzle (#10), intercepting the former wrap-to-#1. Fires EVERY time
// (all players, not just first-run onboarding). Reuses the warm-up dialog
// chrome + the same six bullets (WARM_UP_COMPLETE_LEAD). Row fork: a small
// de-emphasised secondary ("practice again") on the left goes to practice
// puzzle 1 (what the NEXT wrap-around would have done), with the large blue
// primary ("Let's Play") on the right routing to the daily. Autofocus lands on the primary
// "Let's Play". Escape falls through to Play (never soft-lock), matching the
// warm-up congrats convention.
export function showPracticeComplete(
  onPlay: () => void,
  onPracticeAgain: () => void,
): void {
  showWarmUpDialog({
    titleId: 'practice-complete-title',
    title: PRACTICE_COMPLETE_TEXT,
    lead: WARM_UP_COMPLETE_LEAD,
    buttons: [
      { label: 'practice again', action: onPracticeAgain, secondary: true },
      { label: "Let's Play", action: onPlay, autofocus: true },
    ],
    onClose: onPlay,
  })
}
